'use client';

import Link from 'next/link';
import type { JSX } from 'react';
import { Trans, useTranslation } from 'react-i18next';

/**
 * Three featured-story hero variants for the blog landing. The
 * active one is driven by the host's
 * {@code BlogModuleConfig.blogHeroVariant}.
 *
 * <ul>
 *   <li>{@code gradient}: colored gradient block, headline below.
 *       Default — works for hosts without strong cover photography.</li>
 *   <li>{@code cover-photo}: full-bleed cover, knocked-out white
 *       headline over a dark gradient. Works for hosts with
 *       editorial photography.</li>
 *   <li>{@code serif-split}: 60/40 split — serif headline left, cover
 *       right. Editorial / magazine register.</li>
 * </ul>
 *
 * <p>Cover-string handling is internal to this file because each
 * variant uses a slightly different gradient composition — sharing
 * via {@link coverGradientBackground} would flatten those nuances.
 */
export type BlogHeroVariant = 'gradient' | 'cover-photo' | 'serif-split';

export interface BlogHeroProps {
  href: string;
  title: string;
  excerpt?: string;
  date?: string;
  author?: string;
  authorRole?: string;
  category?: string;
  readTime?: string;
  cover?: string;
}

function isHexCover(c: string): boolean {
  return c.startsWith('#');
}

function isUrlCover(c: string): boolean {
  return /^(https?:\/\/|\/\/|\/|data:)/i.test(c);
}

/**
 * Flat (un-gradiented) cover background — accepts a hex colour, a URL,
 * or a raw CSS background string. Exported for the article-detail
 * header in {@code BlogDetailLayout}, which shows the cover as-is
 * rather than as a teaser gradient.
 */
export function flatCoverBg(cover: string | undefined): string {
  if (!cover) return 'var(--color-neutral-200, #e4e4e7)';
  if (isHexCover(cover)) return cover;
  if (isUrlCover(cover)) return `url(${cover}) center/cover no-repeat`;
  return cover;
}

function gradientCoverBg(cover: string | undefined): string {
  if (!cover) {
    return 'linear-gradient(135deg, var(--color-brand-500, #4f46e5), var(--color-brand-700, #312e81))';
  }
  if (isHexCover(cover)) {
    return `linear-gradient(135deg, ${cover}, color-mix(in srgb, ${cover} 60%, #000))`;
  }
  if (isUrlCover(cover)) {
    return `linear-gradient(135deg, rgba(0,0,0,0.15), rgba(0,0,0,0.45)), url(${cover}) center/cover no-repeat`;
  }
  return cover;
}

function coverPhotoBg(cover: string | undefined): string {
  const overlay =
    'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 60%, rgba(0,0,0,0) 100%)';
  if (!cover) return `${overlay}, #0a0a0a`;
  if (isHexCover(cover)) {
    return `${overlay}, linear-gradient(135deg, ${cover}, color-mix(in srgb, ${cover} 50%, #000))`;
  }
  if (isUrlCover(cover)) {
    return `${overlay}, url(${cover}) center/cover no-repeat`;
  }
  return `${overlay}, ${cover}`;
}

export function resolveBlogHero(
  variant: BlogHeroVariant | undefined,
): (props: BlogHeroProps) => JSX.Element {
  if (variant === 'cover-photo') return BlogHeroCoverPhoto;
  if (variant === 'serif-split') return BlogHeroSerifSplit;
  return BlogHeroGradient;
}

export function BlogHeroGradient({
  href,
  title,
  excerpt,
  date,
  author,
  category,
  readTime,
  cover,
}: BlogHeroProps) {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <Link
      href={href}
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        marginBottom: 32,
      }}
      // className="lms-blog-hero lms-blog-hero--gradient"
    >
      <div
        aria-hidden
        style={{
          aspectRatio: '21 / 9',
          background: gradientCoverBg(cover),
          borderRadius: 'var(--radius-md, 8px)',
          marginBottom: 16,
        }}
      />
      <span
        style={{
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: 'var(--font-tracking-widest, 0.14em)',
          textTransform: 'uppercase',
          color: 'var(--color-brand-600, #4f46e5)',
        }}
      >
        {t('blog.hero.featured')}
        {category ? ` · ${category}` : ''}
      </span>
      <h2
        style={{
          margin: 'var(--space-2, 8px) 0',
          fontSize: 'clamp(var(--font-size-2xl, 28px), 3vw, var(--font-size-3xl, 36px))',
          fontWeight: 700,
          lineHeight: 1.15,
          letterSpacing: 'var(--font-tracking-snug, -0.015em)',
        }}
      >
        {title}
      </h2>
      {excerpt && (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-md, 16px)',
            lineHeight: 1.5,
            color: 'var(--color-text-secondary, #71717a)',
            maxWidth: 720,
          }}
        >
          {excerpt}
        </p>
      )}
      <MetaLine author={author} date={date} readTime={readTime} />
    </Link>
  );
}

export function BlogHeroCoverPhoto({
  href,
  title,
  excerpt,
  author,
  category,
  readTime,
  cover,
}: BlogHeroProps) {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <Link
      href={href}
      style={{
        display: 'block',
        position: 'relative',
        textDecoration: 'none',
        color: 'var(--color-text-on-brand, #fff)',
        marginBottom: 32,
        aspectRatio: '21 / 9',
        background: coverPhotoBg(cover),
        borderRadius: 'var(--radius-md, 8px)',
        overflow: 'hidden',
      }}
      className="lms-blog-hero lms-blog-hero--cover-photo"
    >
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: 'clamp(20px, 4vw, 48px)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div
          style={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: 'var(--font-tracking-widest, 0.18em)',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.85)',
          }}
        >
          {t('blog.hero.featured')}
          {category ? ` · ${category}` : ''}
        </div>
        <h2
          style={{
            margin: 0,
            fontSize: 'clamp(28px, 4vw, 44px)',
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: 'var(--font-tracking-snug, -0.02em)',
            color: 'var(--color-text-on-brand, #fff)',
            textShadow: '0 2px 12px rgba(0,0,0,0.3)',
            maxWidth: 800,
          }}
        >
          {title}
        </h2>
        {excerpt && (
          <p
            style={{
              margin: 0,
              fontSize: 'clamp(14px, 1.5vw, 17px)',
              lineHeight: 1.5,
              color: 'rgba(255,255,255,0.85)',
              maxWidth: 640,
            }}
          >
            {excerpt}
          </p>
        )}
        {(author || readTime) && (
          <div
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              color: 'rgba(255,255,255,0.7)',
              marginTop: 4,
            }}
          >
            {author && <span>{author}</span>}
            {author && readTime && ' · '}
            {readTime && <span>{readTime}</span>}
          </div>
        )}
      </div>
    </Link>
  );
}

export function BlogHeroSerifSplit({
  href,
  title,
  excerpt,
  author,
  authorRole,
  category,
  readTime,
  cover,
}: BlogHeroProps) {
  const { t } = useTranslation('site-builder-blocks');
  return (
    <Link
      href={href}
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        marginBottom: 40,
      }}
      className="lms-blog-hero lms-blog-hero--serif-split"
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 3fr) minmax(0, 2fr)',
          gap: 32,
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div
            style={{
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: 'var(--font-tracking-widest, 0.16em)',
              textTransform: 'uppercase',
              color: 'var(--color-brand-600, #4f46e5)',
            }}
          >
            {t('blog.hero.featured')}
            {category ? ` · ${category}` : ''}
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: 'clamp(30px, 3.5vw, 44px)',
              fontWeight: 500,
              lineHeight: 1.1,
              letterSpacing: 'var(--font-tracking-snug, -0.02em)',
              fontFamily: 'var(--font-family-serif, "Source Serif Pro", Georgia, serif)',
            }}
          >
            {title}
          </h2>
          {excerpt && (
            <p
              style={{
                margin: 0,
                fontSize: 'var(--font-size-md, 16px)',
                lineHeight: 1.55,
                color: 'var(--color-text-secondary, #71717a)',
                fontStyle: 'italic',
                maxWidth: 600,
              }}
            >
              {excerpt}
            </p>
          )}
          {author && (
            <div
              style={{
                fontSize: 'var(--font-size-xs, 12px)',
                color: 'var(--color-text-secondary, #71717a)',
                marginTop: 4,
                letterSpacing: 'var(--font-tracking-wider, 0.04em)',
              }}
            >
              <Trans
                t={t}
                i18nKey="blog.hero.byline"
                values={{ author }}
                components={{ a: <span style={{ color: 'var(--color-text-primary, #111)' }} /> }}
              />
              {authorRole && <> · {authorRole}</>}
              {readTime && ` · ${readTime}`}
            </div>
          )}
        </div>
        <div
          aria-hidden
          style={{
            aspectRatio: '5 / 4',
            background: flatCoverBg(cover),
            borderRadius: 'var(--radius-md, 8px)',
          }}
        />
      </div>
    </Link>
  );
}

function MetaLine({
  author,
  date,
  readTime,
}: {
  author?: string;
  date?: string;
  readTime?: string;
}) {
  const parts: string[] = [];
  if (author) parts.push(author);
  if (date) parts.push(date);
  if (readTime) parts.push(readTime);
  if (parts.length === 0) return null;
  return (
    <div style={{ marginTop: 12, fontSize: '13px', color: 'var(--color-text-secondary, #71717a)' }}>
      {parts.join(' · ')}
    </div>
  );
}
